/**
 * Screen Wake Lock while offline downloads run (iPadOS ≥ 18.4; elsewhere best effort). The lock is
 * dropped by the browser when the app is hidden, so it is re-requested when it becomes visible.
 */
interface WakeLockSentinelLike {
  released: boolean;
  release(): Promise<void>;
}
type WakeLockNavigator = Navigator & { wakeLock?: { request(type: 'screen'): Promise<WakeLockSentinelLike> } };

let wanted = false;
let sentinel: WakeLockSentinelLike | null = null;
let listening = false;

async function acquire(): Promise<void> {
  const nav = navigator as WakeLockNavigator;
  if (!wanted || !nav.wakeLock || document.visibilityState !== 'visible') return;
  if (sentinel && !sentinel.released) return;
  try {
    sentinel = await nav.wakeLock.request('screen');
    if (!wanted) void release();
  } catch {
    sentinel = null; // not allowed (e.g. low power mode) — downloads continue anyway
  }
}

async function release(): Promise<void> {
  const s = sentinel;
  sentinel = null;
  if (s && !s.released) await s.release().catch(() => undefined);
}

function onVisibility(): void {
  if (document.visibilityState === 'visible') void acquire();
}

export function holdWakeLock(on: boolean): void {
  if (typeof document === 'undefined') return;
  wanted = on;
  if (on) {
    if (!listening) {
      document.addEventListener('visibilitychange', onVisibility);
      listening = true;
    }
    void acquire();
  } else {
    if (listening) {
      document.removeEventListener('visibilitychange', onVisibility);
      listening = false;
    }
    void release();
  }
}
